"use client";
import { Box, Button, Heading, Text, VStack, Image } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppTheme } from "../ThemeContext.tsx";

type EmailPurpose = "signup" | "passwordReset";

const CheckYourEmail = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();
  const navigate = useNavigate();

  // Define colors based on theme
  const outerBg = isDark ? "gray.800" : "gray.100";
  const innerBg = isDark ? "gray.700" : "white";
  const headingColor = isDark ? "white" : "black";
  const textColor = isDark ? "gray.300" : "gray.600";

  const email = location.state?.email;
  const purpose: EmailPurpose = location.state?.purpose || "signup";
  const dashboardIconUrl = isDark ? process.env.REACT_APP_DASHBOARD_DARK_ICON_URL || "": process.env.REACT_APP_DASHBOARD_LIGHT_ICON_URL || "";

  const getTitle = () => {
    return purpose === "signup" ? "Confirm Your Email" : "Reset Your Password";
  };

  const getMessage = () => {
    return purpose === "signup"
      ? `We've sent a confirmation link to ${email}. Please check your email and click the link to verify your account.`
      : `We've sent a password reset link to ${email}. Please check your email and click the link to reset your password.`;
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (!email) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg={outerBg}
      >
        <Box
          width="full"
          maxWidth="md"
          p={8}
          borderRadius="md"
          bg={innerBg}
          boxShadow="lg"
        >
          <VStack spacing={6}>
            {dashboardIconUrl && (
              <Image
                src={dashboardIconUrl || "/placeholder.svg"}
                alt="Logo"
                maxH="60px"
                mb={4}
              />
            )}
            <Heading color={headingColor}>Error</Heading>
            <Text color={textColor}>Email information is missing.</Text>
            <Button
              onClick={handleBackToLogin}
              colorScheme="gray"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Back to Login
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={outerBg}
    >
      <Box
        width="full"
        maxWidth="md"
        p={8}
        borderRadius="md"
        bg={innerBg}
        boxShadow="lg"
      >
        <VStack spacing={6}>
          {dashboardIconUrl && (
            <Image
              src={dashboardIconUrl || "/placeholder.svg"}
              alt="Logo"
              maxH="60px"
              mb={4}
            />
          )}
          <Heading color={headingColor}>{getTitle()}</Heading>
          <Text color={textColor} textAlign="center">
            {getMessage()}
          </Text>
          <Button
            onClick={handleBackToLogin}
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Back to Login
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default CheckYourEmail;
